/**
 * Itens do menu lateral e ícones inline — extraído do Shell (#336) para o
 * conjunto de itens poder ser testado sem montar o Shell (React puro).
 */

export type SubItem = {
  path: string
  labelKey: string
  icon: (props: { className?: string }) => React.ReactElement
  roles: string[]
  /** testid opcional para o E2E (ex.: 'nav-translations') */
  testId?: string
}

export const SUB_ITEMS: SubItem[] = [
  {
    path: 'dashboard',
    labelKey: 'admin.nav.dashboard',
    icon: HomeIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    path: 'kitchen',
    labelKey: 'admin.nav.kitchen',
    icon: FlameIcon,
    roles: ['kitchen', 'manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    path: 'waiter',
    labelKey: 'admin.nav.waiter',
    icon: BellIcon,
    roles: ['waiter', 'manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    // #274/#284: caixa de aprovação de ajustes de preço + auditoria
    path: 'adjustments',
    labelKey: 'admin.nav.adjustments',
    icon: TagIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    path: 'branding',
    labelKey: 'admin.nav.branding',
    icon: PaintIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    // #229: Settings → Localização (endereço + mapa + reconfirmação)
    path: 'location',
    labelKey: 'admin.nav.location',
    icon: MapPinIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
    testId: 'nav-location',
  },
  {
    path: 'menu',
    labelKey: 'admin.nav.menu',
    icon: MenuIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    // #216: fila «Revisar atributos» — alergénios + dietas + picância + tags
    path: 'review-attributes',
    labelKey: 'admin.nav.review_attributes',
    icon: CheckCircleIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
    testId: 'nav-review-attributes',
  },
  {
    // #336: traduções do menu (idiomas, execuções, refresh) fora do Branding
    path: 'translations',
    labelKey: 'admin.nav.translations',
    icon: TranslateIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
    testId: 'nav-translations',
  },
  {
    path: 'import',
    labelKey: 'admin.import.nav',
    icon: CameraIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    path: 'combos',
    labelKey: 'admin.nav.combos',
    icon: TagIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    path: 'tax-rules',
    labelKey: 'admin.nav.taxes',
    icon: PercentIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    path: 'tables',
    labelKey: 'admin.nav.tables',
    icon: TableIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    path: 'kitchens',
    labelKey: 'admin.nav.kitchens',
    icon: StationsIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    path: 'users',
    labelKey: 'admin.nav.users',
    icon: UsersIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
  },
  {
    // #160: pagamento online da conta (Stripe Connect) — onboarding/estado
    path: 'payments',
    labelKey: 'admin.nav.payments',
    icon: CardIcon,
    roles: ['manager', 'restaurant_owner', 'platform_owner'],
    testId: 'nav-payments',
  },
]

/** Pino de localização (#229). */
function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
      />
    </svg>
  )
}

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12l8.954-8.955a.75.75 0 011.06 0L21 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
      />
    </svg>
  )
}
function FlameIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.362 5.214A8.252 8.252 0 0112 15 8.25 8.25 0 006.378 6.682m8.984-1.468A8.25 8.25 0 003.75 12a8.25 8.25 0 008.25 8.25 8.196 8.196 0 003.362-.688 8.25 8.25 0 000-15.034z"
      />
    </svg>
  )
}
function BellIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
      />
    </svg>
  )
}
function PaintIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 13.5A3.375 3.375 0 0110.125 10.5l5.625-5.625a2.813 2.813 0 00-3.977-3.977L6.148 6.523A3.375 3.375 0 003 9.75m.75 7.5a.75.75 0 000 1.5"
      />
    </svg>
  )
}
function MenuIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25A2.25 2.25 0 0113.5 8.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
      />
    </svg>
  )
}
/** #336: traduções — letra "A" com traços de texto (idioma/translate). */
function TranslateIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 5.25h7.5M6.75 3v2.25m0 0C6.75 8.75 5 11.5 2.5 13.25m1-5.75c.9 3 2.9 5.4 5.75 6.75M10.5 17l3.75-8.5L18 17m-6.1-3h5.2"
      />
    </svg>
  )
}
function CameraIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"
      />
    </svg>
  )
}

/** #216: revisar atributos — círculo com visto. */
function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  )
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L9.568 3z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  )
}
function PercentIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"
      />
    </svg>
  )
}
/** Estações de produção (cozinhas) — três blocos lado a lado. */
function StationsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
    >
      <rect x="3" y="4" width="8" height="7" rx="1.5" />
      <rect x="13" y="4" width="8" height="7" rx="1.5" />
      <rect x="3" y="13" width="8" height="7" rx="1.5" />
      <rect x="13" y="13" width="8" height="7" rx="1.5" />
    </svg>
  )
}

function TableIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 5.25h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5"
      />
    </svg>
  )
}
function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"
      />
    </svg>
  )
}
function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
      />
    </svg>
  )
}
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
      />
    </svg>
  )
}
function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  )
}
function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function CollapseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.5v15m0-15h12" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 9.75L9.75 13.5m0-3.75v3.75h3.75"
        transform="translate(-1 -2)"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7.5L11.25 11.25 15 15" />
    </svg>
  )
}
function ExpandIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 6.75L8.25 3M4.5 6.75L8.25 10.5M4.5 6.75H15"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.5v15" opacity="0" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.75 13.5v3.75h-3.75" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.75 17.25L13 13.5" />
    </svg>
  )
}

function CardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 8h15" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.5 12h4" />
    </svg>
  )
}

/** Ícones usados pelo próprio Shell (fora da lista de sub-itens). */
export {
  ChevronRightIcon,
  ChevronDownIcon,
  LogoutIcon,
  BuildingIcon,
  CollapseIcon,
  ExpandIcon,
  UsersIcon,
}
